import { useMemo, useState } from "react";
import { ChevronDown, History } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { taskStatusColor, BRAND_COLORS, withAlpha } from "../lib/bookingStatus";
import { deDateMediumTime } from "../lib/dateFormat";

/**
 * Discrete Status-Historie timeline for the BookingDetailModal.
 *
 * Renders `booking.status_history` (Feb 2026) — one row per DB-persisted
 * status flip with human-readable transition, timestamp, actor + reason
 * badge (auto-recompute · item-unchecked · all-items-done · cancel).
 *
 * Feb 2026 (Follow-up) · Farb-Zuweisung zentralisiert über `taskStatusColor`
 * aus `lib/bookingStatus`. Keine lokalen Hex-Codes mehr — `confirmed` läuft
 * jetzt korrekt auf BLAU (via TASK_STATUS_COLOR), nicht mehr Gold.
 */

const STATUS_LABEL_DE = {
  requested:   "Angefragt",
  confirmed:   "Bestätigt",
  in_progress: "In Betreuung",
  completed:   "Erledigt",
  cancelled:   "Storniert",
};
const STATUS_LABEL_EN = {
  requested:   "Requested",
  confirmed:   "Confirmed",
  in_progress: "In service",
  completed:   "Completed",
  cancelled:   "Cancelled",
};

const REASON_LABEL_DE = {
  "all-items-done":  "Alle Aufgaben abgehakt",
  "item-unchecked":  "Haken entfernt",
  "auto-recompute":  "Auto-Prüfung",
  "cancel":          "Storno",
};
const REASON_LABEL_EN = {
  "all-items-done":  "All tasks done",
  "item-unchecked":  "Item unchecked",
  "auto-recompute":  "Auto-check",
  "cancel":          "Cancellation",
};

function fmt(iso, de) {
  if (!iso) return "—";
  try {
    return deDateMediumTime(iso);
  } catch { return iso; }
}
function rel(iso, de) {
  if (!iso) return "";
  try {
    const diffMs = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return de ? "gerade eben" : "just now";
    if (mins < 60) return de ? `vor ${mins} Min.` : `${mins} min ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return de ? `vor ${hours} Std.` : `${hours} h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return de ? `vor ${days} ${days === 1 ? "Tag" : "Tagen"}` : `${days} ${days === 1 ? "day" : "days"} ago`;
    return "";
  } catch { return ""; }
}

export default function BookingStatusHistory({ history }) {
  const { lang } = useI18n();
  const de = lang !== "en";
  const STATUS_LABEL = de ? STATUS_LABEL_DE : STATUS_LABEL_EN;
  const REASON_LABEL = de ? REASON_LABEL_DE : REASON_LABEL_EN;
  const [open, setOpen] = useState(false);
  // Chronological ASC → render newest last so the eye follows time downward.
  const rows = useMemo(() => {
    if (!Array.isArray(history) || history.length === 0) return [];
    return history.slice().sort((a, b) => (a.changed_at || "").localeCompare(b.changed_at || ""));
  }, [history]);

  if (rows.length === 0) return null;
  const latest = rows[rows.length - 1];

  return (
    <div
      className="border-t border-navy/10 pt-5 mt-2"
      data-testid="booking-status-history"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between text-left group"
        data-testid="booking-status-history-toggle"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2">
          <History className="w-3.5 h-3.5 text-navy-dark/50" strokeWidth={1.6} />
          <span className="label-caps text-navy-dark/70">{de ? "Status-Historie" : "Status history"}</span>
          <span
            className="inline-flex items-center justify-center min-w-[20px] px-1.5 py-0.5 rounded-full text-[10px] font-medium tabular-nums"
            style={{ background: withAlpha(BRAND_COLORS.navy, 0.06), color: BRAND_COLORS.navy }}
            data-testid="booking-status-history-count"
          >
            {rows.length}
          </span>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-navy-dark/55">
          <span className="hidden md:inline">
            {STATUS_LABEL[latest.status] || latest.status} · {rel(latest.changed_at, de)}
          </span>
          <ChevronDown
            className={`w-3.5 h-3.5 text-navy-dark/40 transition-transform ${open ? "rotate-180" : ""}`}
            strokeWidth={1.8}
          />
        </div>
      </button>

      {open && (
        <ol
          className="mt-4 relative pl-4"
          data-testid="booking-status-history-list"
        >
          {/* Vertical spine — subtle gold, matches the calendar-legend accent. */}
          <span
            aria-hidden="true"
            className="absolute left-1 top-1 bottom-1 w-px"
            style={{ background: withAlpha(BRAND_COLORS.gold, 0.35) }}
          />
          {rows.map((r, i) => {
            // Zentrale Farbzuweisung: taskStatusColor mappt jeden Status auf
            // exakt eine der 4 System-Farben (Grau/Blau/Grün/Rot).
            const { bg: dot } = taskStatusColor(r.status);
            const isSystem = (r.changed_by_role || "").toLowerCase() === "system" || !r.changed_by;
            return (
              <li
                key={`${r.changed_at}-${i}`}
                className="relative pb-3 last:pb-0"
                data-testid={`booking-status-history-row-${i}`}
                data-status={r.status}
              >
                <span
                  aria-hidden="true"
                  className="absolute -left-[13px] top-1 w-2.5 h-2.5 rounded-full"
                  style={{
                    background: dot,
                    boxShadow: `0 0 0 2px ${BRAND_COLORS.ivory}, 0 0 0 3px ${dot}30`,
                  }}
                />
                <div className="flex items-baseline flex-wrap gap-x-2 gap-y-0.5">
                  <span className="text-[12.5px] font-medium text-navy">
                    {r.from_status
                      ? <>{STATUS_LABEL[r.from_status] || r.from_status} <span className="text-navy-dark/40">→</span> {STATUS_LABEL[r.status] || r.status}</>
                      : (STATUS_LABEL[r.status] || r.status)}
                  </span>
                  {r.reason && REASON_LABEL[r.reason] && (
                    <span
                      className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9.5px] tracking-[0.06em] uppercase"
                      style={{ background: withAlpha(BRAND_COLORS.gold, 0.10), color: BRAND_COLORS.goldDark }}
                      data-testid={`booking-status-history-reason-${i}`}
                    >
                      {REASON_LABEL[r.reason]}
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-navy-dark/55 mt-0.5">
                  {fmt(r.changed_at, de)}
                  {" · "}
                  <span data-testid={`booking-status-history-actor-${i}`}>
                    {isSystem ? "System" : (r.changed_by_name || "—")}
                  </span>
                  {r.changed_by_role && !isSystem && (
                    <span className="text-navy-dark/40"> · {r.changed_by_role}</span>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
